import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import classes from './clientlist.scss';
import { Link } from 'react-router-dom';
import resolveRoute from 'lib/resolve-route';
import st from 'core/shared-translations';
import { DataTableHeaderV2, DataTablePaginationV2, Select, Option, DataTableBody, TooltipV3 } from '@getgo/chameleon-web-react-wrapper';
import {
  Typography ,
  Button ,
  DataTableV2,
  DataTableCellV2,
  DataTableRowV2,
  DataTableHeaderCellV2,
  IconButton
 } from '@getgo/chameleon-web-react-wrapper';
import {
  EditOutlinedIcon,
  DoubleChevronLeftFilledIcon,
  ChevronLeftFilledIcon,
  ChevronRightIcon,
  DoubleChevronRightFilledIcon,
  CopyOutlinedIcon} from '@getgo/chameleon-icons/react';
import { Box } from '@mui/system';
import { flexRender, getCoreRowModel, getPaginationRowModel, useReactTable } from '@tanstack/react-table';

const t = defineMessages({
  'client-info-view.no.redirectURIs': {
    id: 'client-info-view.no.redirectURIs',
    defaultMessage: 'No redirect URIs',
    description: 'Text to display when no redirect URIs are specified for a client',
  },
  'client-list.table.title1':{
    id: 'client-list.table.title1',
    defaultMessage: 'Client name',
    description: 'Title for table'
  },
  'client-list.table.title2':{
    id: 'client-list.table.title2',
    defaultMessage: 'Creation date',
    description: 'Title for table'
  },
  'client-list.table.title3':{
    id: 'client-list.table.title3',
    defaultMessage: 'Redirect URIs',
    description: 'Title for table'
  },
  'client-list.table.title4':{
    id: 'client-list.table.title4',
    defaultMessage: 'Scopes',
    description: 'Title for table'
  },
  'client-list.table.title5':{
    id: 'client-list.table.title5',
    defaultMessage: 'Client ID',
    description: 'Title for table'
  },
  'client-info-view.redirectUris.count': {
    id: 'client-info-view.redirectUris.count',
    defaultMessage: '{firstURI} and {remainingCount} more',
    description: 'Label to show the first URI and how many redirect URIs are not shown',
  },
  'client-info-view.no.scopes': {
    id: 'client-info-view.no.scopes',
    defaultMessage: 'No scopes',
    description: 'Text to display when no scopes are specified for a client',
  },

});

const TableView = ({
  clientList,
  onCopy,
}) => {
  const intl = useIntl();
  const columns = [
    { header: <FormattedMessage {...t['client-list.table.title1']} />, accessorKey: "name" , cell: (props) => <Typography>{props.getValue()}</Typography>},
    { header: <FormattedMessage {...t['client-list.table.title2']} />, accessorKey: "clientStartDate",cell: (props) => <Typography>{props.getValue()}</Typography>},
    { header: <FormattedMessage {...t['client-list.table.title3']} />, accessorKey: "redirectUris",
      cell: (props) =>
        <Box>
          <Typography id="redirect-uri-trigger" nowrap>
            {props.getValue() && props.getValue().length < 2 &&
          props.getValue()[0]}
            {props.getValue() && props.getValue().length > 1 &&
              <FormattedMessage
                {...t['client-info-view.redirectUris.count']}
                values={{firstURI: props.getValue()[0], remainingCount: props.getValue().length - 1}} />}
            {!props.getValue() || !props.getValue().length &&
              <FormattedMessage {...t['client-info-view.no.redirectURIs']} />}
          </Typography>
          <TooltipV3 maxWidth="200" triggerId="redirect-uri-trigger" position="bottom-start">
            <Box>
              {(props.getValue() && props.getValue().length) ?
                props.getValue().map((value, index) => (
                  <React.Fragment key={index}>
                    {value}
                    {index < props.getValue().length - 1 && <br />}
                  </React.Fragment>
                )) : intl.formatMessage(t['client-info-view.no.redirectURIs'])}
            </Box>
          </TooltipV3>
        </Box>},
    { header: <FormattedMessage {...t['client-list.table.title4']} />, accessorKey: "permissionsList",
      cell: (props) =>
        <Box
          id="scopes-trigger"
          className={classes['clientPropValue']}>
          {props.getValue() && !!Object.keys(props.getValue()).length &&
        Object.keys(props.getValue()).map((keyName, index ,array) => (
          <Box
            key={index}
            id={`permission_${props.getValue()[keyName].parentScopeKey}`}
            className={classes['permissionScope']}
          >
            <span className={classes['permissionTitle']}>
              {props.getValue()[keyName].allScopesSelected &&
                <FormattedMessage {...st[props.getValue()[keyName].parentScopeTranslationId]} />}
              {!props.getValue()[keyName].allScopesSelected &&
                <FormattedMessage
                  {...st['shared.client.info.selection.count']}
                  values={{
                    product: intl.formatMessage(st[props.getValue()[keyName].parentScopeTranslationId]),
                    selected: props.getValue()[keyName].numberOfScopesSelected,
                    total: props.getValue()[keyName].numberOfScopesInProduct
                  }} />}
            </span>
            {index < array.length - 1 && ','}
          </Box>
        ))}
          <TooltipV3 triggerId="scopes-trigger">
            {Object.keys(props.getValue()).map((keyName) =>
            intl.formatMessage(st[props.getValue()[keyName].parentScopeTranslationId])).join(", ")}
          </TooltipV3>
          {!Object.keys(props.getValue()).length &&
            <span className={classes['noValueText']}>
              <FormattedMessage {...t['client-info-view.no.scopes']} />
            </span>}
        </Box>},
    { header: <FormattedMessage {...t['client-list.table.title5']} />, accessorKey:"clientId",
      cell: (props) =>
        <Box className={classes['copyCta']}>
          <span>{props.getValue()}</span>
          <Button
            style={{ justifySelf: 'start' }}
            data-testid="copy-button"
            onClick={onCopy(props.getValue())}
            leadingIcon={<CopyOutlinedIcon />}
            variant="tertiary">
            <FormattedMessage {...st['shared.copy']} />
          </Button>
        </Box>
    },
  ];

  const newClientList = clientList ||[];

  const table = useReactTable({
    data: newClientList,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  const firstRowOnPage = table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1;
  const lastRowOnPage = Math.min(
    table.getState().pagination.pageIndex * table.getState().pagination.pageSize + table.getState().pagination.pageSize,
    table.getCoreRowModel().flatRows.length,
  );
  const totalRows = table.getFilteredRowModel().flatRows.length;
  return (
    <Box className={classes["mainContentClientView"]}>
      {!!clientList.length &&
        <DataTableV2 className={classes["controlWidth"]}>
          <DataTableHeaderV2 condensed>
            {table.getHeaderGroups().map((headerGroup,index) =>
              <DataTableRowV2 key={index} style={{ borderTop: "none" }}>
                {headerGroup.headers.map(header=>
                  <DataTableHeaderCellV2 key={`${header.column.columnDef.header}`}>
                    {header.column.columnDef.header}
                  </DataTableHeaderCellV2>
          )}
                <DataTableHeaderCellV2>
                  <FormattedMessage {...st[`shared.actions.title`]} />
                </DataTableHeaderCellV2>
              </DataTableRowV2>
          )}
          </DataTableHeaderV2>
          <DataTableBody>
            {table.getRowModel().rows.map((row,index) => (
              <DataTableRowV2 key={index}>
                {row.getVisibleCells().map((cell) => (
                  <DataTableCellV2 key={cell.id}>
                    {flexRender(cell.column.columnDef.cell,cell.getContext())}
                  </DataTableCellV2>))}
                {row.getVisibleCells().map((cell) => {
            if (cell.column.id === 'clientId') {
              return (
                <DataTableCellV2 key={row.id}>
                  <Link
                    style={{paddingLeft: "20px"}}
                    to={resolveRoute('clients.clientedit', {routeParams: [cell.getValue()]})}
                    state={{ prevPath: location.pathname }}>
                    <IconButton data-testid="edit-icon">
                      <EditOutlinedIcon color="#2C72DE" />
                    </IconButton>
                  </Link>
                </DataTableCellV2>
                );
            }
            return null; // Return null if not the desired column
            })}
              </DataTableRowV2>
        ))}
          </DataTableBody>
        </DataTableV2>}
      <DataTablePaginationV2
        select={
          <Box className={classes['pageSizeView']}>
            <Typography>
              <FormattedMessage {...st['shared.row-per-page']} />
            </Typography>
            <Select
              data-testid="page-size-select"
              value={table.getState().pagination.pageSize.toString()}
              onChange={(e) => {
            table.setPageSize(Number(e.target.value));
              }}
        >
              {[5,10, 20, 30, 40, 50].map((pageSize) => (
                <Option key={pageSize} value={pageSize}>
                  {pageSize}
                </Option>
            ))}
            </Select>
          </Box>
      }
        counterText={`${firstRowOnPage} - ${lastRowOnPage} of ${totalRows}`}
        actions={
          <Box>
            <IconButton
              data-testid="first-page"
              label="Go to first page"
              onClick={() => table.setPageIndex(0)}
              disabled={!table.getCanPreviousPage()}
        >
              <DoubleChevronLeftFilledIcon />
            </IconButton>
            <IconButton
              data-testid="previous-page"
              label="Go to previous page"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
        >
              <ChevronLeftFilledIcon />
            </IconButton>
            <Typography variant="caption-medium-strong" color="type-color-secondary">
              <FormattedMessage {...st["shared.pagination.page"]} /> {table.getState().pagination.pageIndex + 1}/{table.getPageCount()}
            </Typography>
            <IconButton data-testid="next-page" label="Go to next page" onClick={() => table.nextPage()} disabled={!table.getCanNextPage()}>
              <ChevronRightIcon />
            </IconButton>
            <IconButton
              data-testid="last-page"
              label="Go to last page"
              onClick={() => table.setPageIndex(table.getPageCount() - 1)}
              disabled={!table.getCanNextPage()}
        >
              <DoubleChevronRightFilledIcon />
            </IconButton>
          </Box>
      }
    />
    </Box>
  );
};

TableView.propTypes = {
  clientList: PropTypes.array.isRequired,
  onCopy: PropTypes.func.isRequired,
  hasClients: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  getValue: PropTypes.func,
};

export default TableView;


